import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { UserContext } from "../context/UserContext"; // Import UserContext for user authentication
import DiscussionForum from "./DiscussionForum"; // Import DiscussionForum component
import {
  FaSearch,
  FaFileAlt,
  FaBalanceScale,
  FaLink,
  FaComments,
  FaVoteYea,
  FaTimes,
  FaAngleLeft,
  FaAngleRight,
} from "react-icons/fa"; // Import icons from react-icons
import Search from "./Search"; // Import the Search component
import BillVoting from './BillVoting';

const VoteStatus = ({ bill }) => {
  return (
    <div className="vote-status" style={{
      marginBottom: '15px',
      padding: '10px',
      backgroundColor: '#f8f9fa',
      borderRadius: '5px',
      textAlign: 'left'
    }}>
      <div style={{ marginBottom: '5px' }}>
        <strong>Current Vote:</strong> {bill?.previous_vote || 'None'}
      </div>
      
      {/* No votes yet - show initial hint */}
      {!bill?.vote_count && (
        <div style={{ color: '#0d6efd' }}>
          You have 2 chances to vote on this bill. Choose carefully!
        </div>
      )}

      {/* One vote used - show last chance warning */}
      {bill?.vote_count === 1 && (
        <div style={{ color: '#dc3545' }}>
          This is your last chance to vote on this bill!
        </div>
      )}

      {/* All votes used */}
      {bill?.vote_count >= 2 && (
        <div style={{ color: '#28a745' }}>
          You have used all your votes for this bill.
        </div>
      )}
    </div>
  );
};

const ChangeVoteConfirmation = ({ onConfirm, onCancel }) => {
  return (
    <div style={{
      marginTop: '15px',
      padding: '15px',
      backgroundColor: '#fff3cd',
      borderRadius: '5px',
      border: '1px solid #ffeeba'
    }}>
      <p style={{ 
        color: '#856404',
        marginBottom: '15px',
        fontWeight: 'bold'
      }}>
        Do you want to change your vote? You have only one chance to change it.
      </p>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
        <button
          onClick={onConfirm}
          style={{
            padding: '8px 20px',
            backgroundColor: '#ffc107',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Yes, change my vote
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '8px 20px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const FederalBills = () => {
  const [activeInnerTab, setActiveInnerTab] = useState("Summary");
  const [selectedBill, setSelectedBill] = useState(null); // Stores the current bill
  const [bills, setBills] = useState([]); // Stores all fetched bills
  const [currentBillIndex, setCurrentBillIndex] = useState(0); // Index of the currently displayed bill
  const { isLoggedIn, emailId, phoneNo } = useContext(UserContext); // Fetch user login, email, and phone number
  const [vote, setVote] = useState(""); // Tracks the user's vote ("Yes" or "No")
  const [comment, setComment] = useState(""); // Tracks the user's comment
  const [thankYouMessage, setThankYouMessage] = useState(null); // Change to null instead of false
  const [showSearchPopup, setShowSearchPopup] = useState(false); // Controls the Advanced Search popup visibility
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false); // Tracks if the screen is tablet/phone in landscape mode
  const [hoveredTab, setHoveredTab] = useState(null); // Tracks hovered tab for tooltip display
  const [showChangeVoteConfirm, setShowChangeVoteConfirm] = useState(false);

  // Detect screen size and orientation
  useEffect(() => {
    const handleResize = () => {
      setIsMobileOrTablet(window.innerWidth <= 1024); // Phones and tablets have a width <= 1024px
    };
    handleResize(); // Initialize on component mount
    window.addEventListener("resize", handleResize); // Attach event listener for resize
    return () => window.removeEventListener("resize", handleResize); // Cleanup on unmount
  }, []);

  // Fetch bills on component load
  useEffect(() => {
    if (isLoggedIn && emailId && phoneNo) {
      fetchBills();
    }
  }, [isLoggedIn, emailId, phoneNo]);

  // Modified fetchBills function
  const fetchBills = async () => {
    try {
      const response = await axios.get("/api/bills-popup", {
        params: { email_id: emailId, selectedType: "Federal" },
      });
      if (response.status === 200 && response.data.length > 0) {
        setBills(response.data);
        setSelectedBill(response.data[0]); // Set the first bill as default
        setCurrentBillIndex(0); // Reset index when new bills are fetched
      } else {
        alert("No bills found for the selected preferences.");
      }
    } catch (error) {
      console.error("Error fetching bills:", error);
      alert("An error occurred while fetching bills. Please try again.");
    }
  };

  const handleSearchResults = (results) => {
    if (results && results.length > 0) {
      // Ensure search results have required properties
      const processedResults = results.map(bill => ({
        ...bill,
        type: "Federal",
        vote_count: bill.vote_count || 0,
        previous_vote: bill.previous_vote || null
      }));
      
      setBills(processedResults);
      setSelectedBill(processedResults[0]);
      setCurrentBillIndex(0);
      setActiveInnerTab("Summary");
      setVote("");
      setComment("");
      setThankYouMessage(null);
      setShowChangeVoteConfirm(false);
    } else {
      alert("No results found for your search.");
    }
    setShowSearchPopup(false);
  };

  // Add this new function to fetch vote status
  const fetchVoteStatus = async (billTitle) => {
    try {
      const response = await axios.get("/api/get-vote-status", {
        params: {
          bill_title: billTitle,
          phoneNo: phoneNo,
          type: "Federal"
        }
      });
      
      if (response.status === 200) {
        setSelectedBill(prev => ({
          ...prev,
          vote_count: response.data.vote_count || 0,
          previous_vote: response.data.previous_vote || null
        }));
      }
    } catch (error) {
      console.error("Error fetching vote status:", error);
    }
  };

  // Modified navigation handlers
  const handlePreviousBill = () => {
    if (currentBillIndex > 0 && bills.length > 0) {
      const newIndex = currentBillIndex - 1;
      setCurrentBillIndex(newIndex);
      setSelectedBill(bills[newIndex]);
      setActiveInnerTab("Summary");
      setVote("");
      setComment("");
      setThankYouMessage(null);
      setShowChangeVoteConfirm(false);
    }
  };

  const handleNextBill = () => {
    if (currentBillIndex < bills.length - 1 && bills.length > 0) {
      const newIndex = currentBillIndex + 1;
      setCurrentBillIndex(newIndex);
      setSelectedBill(bills[newIndex]);
      setActiveInnerTab("Summary");
      setVote("");
      setComment("");
      setThankYouMessage(null);
      setShowChangeVoteConfirm(false);
    }
  };

  const handleGoogleLinksClick = () => {
    if (selectedBill) {
      const googleSearchUrl = `https://www.google.com/search?q=news+on+${encodeURIComponent(selectedBill.bill_title)}`;
      window.open(googleSearchUrl, '_blank');
    } else {
      alert('No bill selected for Google search.');
    }
  };

  // Reset voting state when the selected bill changes
  useEffect(() => {
    setVote(""); // Clear the vote selection
    setComment(""); // Clear the comment input
    setThankYouMessage(null); // Reset thank-you message
    setShowChangeVoteConfirm(false); // Reset change vote confirmation
  }, [selectedBill, activeInnerTab]); // Runs when the user switches to a new bill or enters the Voting tab
  
  const handleVoteChange = (value) => {
    setVote(value);
    setThankYouMessage(null); // Reset Thank You message on vote change
  };

  const handleVoteSubmit = async () => {
    if (!vote) {
      alert("Please select a vote option before submitting.");
      return;
    }
    try {
      const payload = {
        bill: selectedBill.bill_title,
        phoneNo,
        vote,
        comment,
        type: "Federal" // Add explicit type for Federal bills
      };
      const response = await axios.post(
        "/api/submit-eligible-vote",
        payload
      );

      if (response.status === 201 || response.status === 200) {
        const { voteCount } = response.data;
        
        // Update thank you message based on vote count
        if (voteCount >= 2) {
          setThankYouMessage("Thank you for your final vote on this bill!");
        } else {
          setThankYouMessage(`Thank you for voting ${vote}! You have one more vote remaining.`);
        }
        
        // Update selected bill with new vote info
        setSelectedBill({
          ...selectedBill,
          vote_count: voteCount,
          previous_vote: vote
        });
      }
    } catch (error) {
      if (error.response?.status === 400) {
        alert(error.response.data.message || "You have already voted twice for this bill.");
      } else {
        console.error("Error submitting vote:", error);
        alert("An error occurred while submitting your vote.");
      }
    }
  };

  const tabs = [
    { id: "Summary", label: "Summary", icon: <FaFileAlt /> },
    { id: "Pros & Cons", label: "Pros & Cons", icon: <FaBalanceScale /> },
    { id: "Links", label: "Links", icon: <FaLink /> },
    { id: "Discussion Forum", label: "Discussion Forum", icon: <FaComments /> },
  ];

  // Add a useEffect to fetch vote status when selectedBill changes
  useEffect(() => {
    if (selectedBill?.bill_title && activeInnerTab === "Voting") {
      fetchVoteStatus(selectedBill.bill_title);
    }
  }, [selectedBill?.bill_title, activeInnerTab]);

  // Add debug logging
  useEffect(() => {
    console.log('Current Bills:', bills);
    console.log('Current Index:', currentBillIndex);
    console.log('Selected Bill:', selectedBill);
  }, [bills, currentBillIndex, selectedBill]);

  if (!isLoggedIn) {
    return (
      <div
        style={{
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#007bff",
          padding: "20px",
          borderRadius: "5px",
        }}
      >
        Please log in to view details about bills.
      </div>
    );
  }

  if (!selectedBill) {
    return (
      <div
        style={{
          textAlign: "center",
          color: "#007bff",
          padding: "20px",
          borderRadius: "5px",
        }}
      >
        Loading bills...
      </div>
    );
  }

  return (
    <div className="federal-bills-container">
      {/* Category Name and Navigation Buttons */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "5px",
          width: "100%",
          padding: "0 20px"
        }}
      >
        {/* Left Arrow Button */}
        <button
          onClick={(e) => {
            e.preventDefault(); // Prevent default behavior
            e.stopPropagation(); // Stop event bubbling
            if (currentBillIndex > 0) {
              const newIndex = currentBillIndex - 1;
              setCurrentBillIndex(newIndex);
              setSelectedBill(bills[newIndex]);
              setActiveInnerTab("Summary");
              setVote("");
              setComment("");
              setThankYouMessage(null);
              setShowChangeVoteConfirm(false);
            }
          }}
          disabled={currentBillIndex === 0}
          style={{
            width: isMobileOrTablet ? "45px" : "35px",
            height: isMobileOrTablet ? "45px" : "35px",
            backgroundColor: currentBillIndex === 0 ? "#ccc" : "red",
            border: "2px solid red",
            borderRadius: "50%",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: currentBillIndex === 0 ? "not-allowed" : "pointer",
            zIndex: 10,
            flexShrink: 0, // Prevent button from shrinking
          }}
        >
          <FaAngleLeft size={isMobileOrTablet ? 28 : 24} style={{ color: "white" }} />
        </button>

        {/* Category Name and Bill Title */}
        <div style={{ flex: 1, margin: "0 15px" }}>
          {window.innerWidth <= 926 ? (
            // Phone layout - vertical alignment
            <div style={{ 
              width: '100%', 
              display: 'flex', 
              flexDirection: 'column',  // Changed to column layout
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px'  // Reduced gap between elements
            }}>
              <h4
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: "white",
                  margin: 0,
                  textAlign: 'center'  // Center align text
                }}
              >
                {selectedBill.category_name}
              </h4>
              <p
                style={{
                  margin: 0,
                  fontSize: "16px",
                  color: "#fff",
                  textAlign: 'center'  // Center align text
                }}
              >
                {selectedBill.bill_title}
              </p>
            </div>
          ) : (
            // Laptop layout - remains unchanged
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                margin: "5px 0",
              }}
            >
              <span style={{ fontSize: "20px", fontWeight: "bold" }}>
                {selectedBill.category_name}
              </span>
              <span style={{ margin: "0 10px", fontSize: "20px" }}>:</span>
              <span style={{ fontSize: "18px" }}>
                {selectedBill.bill_title}
              </span>
            </div>
          )}
        </div>

        {/* Right Arrow Button */}
        <button
          onClick={(e) => {
            e.preventDefault(); // Prevent default behavior
            e.stopPropagation(); // Stop event bubbling
            if (currentBillIndex < bills.length - 1) {
              const newIndex = currentBillIndex + 1;
              setCurrentBillIndex(newIndex);
              setSelectedBill(bills[newIndex]);
              setActiveInnerTab("Summary");
              setVote("");
              setComment("");
              setThankYouMessage(null);
              setShowChangeVoteConfirm(false);
            }
          }}
          disabled={currentBillIndex === bills.length - 1}
          style={{
            width: isMobileOrTablet ? "45px" : "35px",
            height: isMobileOrTablet ? "45px" : "35px",
            backgroundColor: currentBillIndex === bills.length - 1 ? "#ccc" : "red",
            border: "2px solid red",
            borderRadius: "50%",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: currentBillIndex === bills.length - 1 ? "not-allowed" : "pointer",
            zIndex: 10,
            flexShrink: 0, // Prevent button from shrinking
          }}
        >
          <FaAngleRight size={isMobileOrTablet ? 28 : 24} style={{ color: "white" }} />
        </button>
      </div>

      {/* Search Icon and Tabs */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: isMobileOrTablet ? "8px" : "15px",
          padding: "5px 0",
          borderBottom: "2px solid #ccc",
        }}
      >
        {/* Search Icon */}
        <div
          onClick={() => setShowSearchPopup(true)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f4f4f4",
            borderRadius: "8px",
            border: "1px solid #ccc",
            color: "#000",
            fontWeight: "bold",
            padding: isMobileOrTablet ? "10px" : "12px",
            cursor: "pointer",
            gap: "5px",
          }}
        >
          <FaSearch />
          {!isMobileOrTablet && <span>Search</span>}
        </div>

        {/* Tabs */}
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`tab ${activeInnerTab === tab.id ? "active" : ""}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              padding: isMobileOrTablet ? "10px" : "12px",
              backgroundColor: activeInnerTab === tab.id ? "#007bff" : "#f4f4f4",
              color: activeInnerTab === tab.id ? "#fff" : "#000",
              fontWeight: !isMobileOrTablet && "bold",
              borderRadius: "8px",
              cursor: "pointer",
              border: "1px solid #ccc",
              fontSize: isMobileOrTablet ? "20px" : "14px",
              minWidth: isMobileOrTablet ? "45px" : "100px",
            }}
            onMouseEnter={() => isMobileOrTablet && setHoveredTab(tab.id)}
            onMouseLeave={() => isMobileOrTablet && setHoveredTab(null)}
            onClick={() => setActiveInnerTab(tab.id)}
          >
            {isMobileOrTablet ? tab.icon : tab.label}
            {isMobileOrTablet && hoveredTab === tab.id && (
              <div
                style={{
                  position: "absolute",
                  top: "-30px",
                  backgroundColor: "#000",
                  color: "#fff",
                  padding: "5px 10px",
                  borderRadius: "5px",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  zIndex: 1000,
                }}
              >
                {tab.label}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Popup for Advanced Search */}
      {showSearchPopup && (
        <div
          style={{
            position: "fixed",
            top: isMobileOrTablet ? "50%" : "60%",
            left: isMobileOrTablet ? "50%" : "35%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
            width: isMobileOrTablet ? "90%" : "500px",
            height: isMobileOrTablet ? "70%" : "auto",
            padding: "20px",
            zIndex: 1000,
            overflowY: isMobileOrTablet ? "auto" : "visible",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
            }}
          >
            <h4 style={{ margin: 0 }}>Advanced Search</h4>
            <FaTimes
              onClick={() => setShowSearchPopup(false)}
              style={{
                cursor: "pointer",
                color: "#000",
              }}
            />
          </div>
          <Search onClose={() => setShowSearchPopup(false)} onSearchResults={handleSearchResults} />
        </div>
      )}

      {/* Tab Content */}
      <div className="inner-tab-content" style={{ marginTop: "5px", borderRadius: "5px" }}>
        {activeInnerTab === "Summary" && (
          <div
            style={{
              backgroundColor: "#fff",
              padding: "5px",
              borderRadius: "5px",
              color: "#000",
              height: `calc(93vh - 55vh)`,
              overflowY: "auto",
            }}
          >
            <h5
              style={{
                textAlign: "center",
                marginBottom: "5px",
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              Summary
            </h5>
            {selectedBill.summary ? (
            selectedBill.summary
              .split(/\n+/) // Split by newline characters
              .filter((paragraph) => paragraph.trim() !== "") // Remove empty paragraphs
              .map((paragraph, index) => (
                <p key={index} style={{ marginBottom: "10px" }}>
                  {paragraph}
                </p>
              ))
          ) : (
            <p>No Summary Available</p>
          )}
          </div>
        )}

        {activeInnerTab === "Pros & Cons" && (
          <div
            style={{
              display: "flex",
              gap: "20px",
              justifyContent: "center",
              marginTop: "5px",
              backgroundColor: "transparent",
            }}
          >
            <div
              style={{
                flex: 1,
                backgroundColor: "#fff",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                color: "#000",
                height: isMobileOrTablet ? "auto" : `calc(93vh - 55vh)`,
                overflowY: isMobileOrTablet ? "visible" : "auto",
              }}
            >
              <h5 style={{ textAlign: "center" }}>Pros</h5>
              {selectedBill.pros ? (
              <ul style={{ paddingLeft: "20px" }}>
                {selectedBill.pros
                  .split("- ") // Split by "- " to create a new line for each point
                  .filter((item) => item.trim() !== "") // Remove empty items
                  .map((item, index) => (
                    <li key={index}>{item.trim()}</li>
                  ))}
              </ul>
            ) : (
              <p>No Pros Available</p>
            )}
            </div>

            <div
              style={{
                flex: 1,
                backgroundColor: "#fff",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                color: "#000",
                height: isMobileOrTablet ? "auto" : `calc(93vh - 55vh)`,
                overflowY: isMobileOrTablet ? "visible" : "auto",
              }}
            >
              <h5 style={{ textAlign: "center" }}>Cons</h5>
              {selectedBill.cons ? (
              <ul style={{ paddingLeft: "20px" }}>
                {selectedBill.cons
                  .split("- ") // Split by "- " to create a new line for each point
                  .filter((item) => item.trim() !== "") // Remove empty items
                  .map((item, index) => (
                    <li key={index}>{item.trim()}</li>
                  ))}
              </ul>
            ) : (
              <p>No Cons Available</p>
            )}
            </div>
          </div>
        )}

        {activeInnerTab === "Links" && (
          <div
            style={{
              display: "flex",
              gap: "20px",
              justifyContent: "center",
              marginTop: "5px",
            }}
          >
            <div
              style={{
                flex: 1,
                backgroundColor: "#fff",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                color: "#000",
                height: `calc(93vh - 55vh)`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
              }}
            >
              <h5 style={{ textAlign: "center" }}>Google Link</h5>
              <button
                onClick={handleGoogleLinksClick}
                style={{
                  backgroundColor: "#007bff",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  padding: "10px 20px",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                Open Google Links
              </button>
            </div>

            <div
              style={{
                flex: 1,
                backgroundColor: "#fff",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                color: "#000",
                height: `calc(93vh - 55vh)`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
              }}
            >
              <h5 style={{ textAlign: "center" }}>PDF Link</h5>
              <a
                href={selectedBill.bill_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  backgroundColor: "#007bff",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  padding: "10px 20px",
                  textDecoration: "none",
                }}
              >
                Open PDF
              </a>
            </div>
          </div>
        )}

        {activeInnerTab === "Discussion Forum" && (
          <DiscussionForum selectedBill={selectedBill?.bill_title || ""} />
        )}
      </div>

      {/* Add BillVoting as a separate section below tabs */}
      <div style={{ marginTop: "5px" }}>
        <BillVoting 
          selectedBill={selectedBill}
          phoneNo={phoneNo}
          onVoteSubmitted={(voteCount, vote) => {
            setSelectedBill(prev => ({
              ...prev,
              vote_count: voteCount,
              previous_vote: vote
            }));
          }}
        />
      </div>
    </div>
  );
};

export default FederalBills;
